import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { addData, deleteData, editData, getData } from "../api/data";
import { hideLoader, setError, setLoader } from "./quantomState";

export const assignmentTypesURI = "assignment-types";

export const fetchAssignmentTypes = createAsyncThunk(
  `${assignmentTypesURI}/fetch`,
  async (params, { dispatch, rejectWithValue }) => {
    try {
      dispatch(setLoader());
      return getData(assignmentTypesURI, params);
    } catch (error) {
      dispatch(
        setError({
          error:
            error.response?.data?.error ||
            `Error fetching ${assignmentTypesURI}`,
          errorTitle: "Fetch Assignment Types Error",
        })
      );
      return rejectWithValue(
        error.response?.data?.error || "Unknown error occurred"
      );
    } finally {
      dispatch(hideLoader());
    }
  }
);

export const fetchAssignmentTypeById = createAsyncThunk(
  `${assignmentTypesURI}/fetch-by-id`,
  async (id, { dispatch, rejectWithValue }) => {
    try {
      dispatch(setLoader());
      return getData(`${assignmentTypesURI}/${id}`);
    } catch (error) {
      dispatch(
        setError({
          error:
            error.response?.data?.error || `Error fetching assignment type`,
          errorTitle: "Fetch Assignment Type Error",
        })
      );
      return rejectWithValue(
        error.response?.data?.error || "Unknown error occurred"
      );
    } finally {
      dispatch(hideLoader());
    }
  }
);

export const addAssignmentType = createAsyncThunk(
  `${assignmentTypesURI}/add`,
  async (assignmentType, { dispatch, rejectWithValue }) => {
    try {
      dispatch(setLoader());
      return await addData(assignmentTypesURI, assignmentType);
    } catch (error) {
      dispatch(
        setError({
          error: error.response?.data?.error || "Error adding assignment type",
          errorTitle: "Add Assignment Type Error",
        })
      );
      return rejectWithValue(
        error.response?.data?.error || "Failed to add assignment type"
      );
    } finally {
      dispatch(hideLoader());
    }
  }
);

export const editAssignmentType = createAsyncThunk(
  `${assignmentTypesURI}/edit`,
  async ({ id, data }, { dispatch, rejectWithValue }) => {
    try {
      dispatch(setLoader());
      await editData(`${assignmentTypesURI}/${id}`, data);

      // Refetch all assignment types
      const result = await getData(assignmentTypesURI);

      return result;
    } catch (error) {
      dispatch(
        setError({
          error: error.response?.data?.error || "Error editing assignment type",
          errorTitle: "Edit Assignment Type Error",
        })
      );
      return rejectWithValue(
        error.response?.data?.error || "Failed to edit assignment type"
      );
    } finally {
      dispatch(hideLoader());
    }
  }
);

export const deleteAssignmentType = createAsyncThunk(
  `${assignmentTypesURI}/delete`,
  async (id, { dispatch, rejectWithValue }) => {
    try {
      dispatch(setLoader());
      await deleteData(`${assignmentTypesURI}/${id}`);
      return { id };
    } catch (error) {
      dispatch(
        setError({
          error:
            error.response?.data?.error || "Error deleting assignment type",
          errorTitle: "Delete Assignment Type Error",
        })
      );
      return rejectWithValue(
        error.response?.data?.error || "Failed to delete assignment type"
      );
    } finally {
      dispatch(hideLoader());
    }
  }
);

const initialState = {
  loading: "idle",
  data: [],
  selectedAssignmentType: null,
};

const assignmentTypesSlice = createSlice({
  name: "assignmentTypes",
  initialState,
  reducers: {
    clearSelectedAssignmentType: (state) => {
      state.selectedAssignmentType = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAssignmentTypes.pending, (state) => {
        state.loading = "pending";
      })
      .addCase(fetchAssignmentTypes.fulfilled, (state, { payload }) => {
        state.data = payload.list;
        state.loading = "idle";
      })
      .addCase(fetchAssignmentTypes.rejected, (state, action) => {
        console.error("Fetch assignment types rejected:", action.error.message);
        state.error = action.error.message;
        state.loading = "idle";
      })
      .addCase(fetchAssignmentTypeById.pending, (state) => {
        state.loading = "pending";
      })
      .addCase(fetchAssignmentTypeById.fulfilled, (state, { payload }) => {
        state.selectedAssignmentType = payload;
        state.loading = "idle";
      })
      .addCase(fetchAssignmentTypeById.rejected, (state, action) => {
        console.error(
          "Fetch assignment type by id rejected:",
          action.error.message
        );
        state.error = action.error.message;
        state.loading = "idle";
      })
      .addCase(addAssignmentType.pending, (state) => {
        state.loading = "pending";
      })
      .addCase(addAssignmentType.fulfilled, (state, { payload }) => {
        state.data.push(payload);
        state.loading = "idle";
      })
      .addCase(addAssignmentType.rejected, (state, action) => {
        console.error("Add assignment type rejected:", action.error.message);
        state.error = action.error.message;
        state.loading = "idle";
      })
      .addCase(editAssignmentType.pending, (state) => {
        state.loading = "pending";
      })
      .addCase(editAssignmentType.fulfilled, (state, { payload }) => {
        state.data = payload.list;
        state.loading = "idle";
      })
      .addCase(editAssignmentType.rejected, (state, action) => {
        console.error("Edit assignment type rejected:", action.error.message);
        state.error = action.error.message;
        state.loading = "idle";
      })
      .addCase(deleteAssignmentType.pending, (state) => {
        state.loading = "pending";
      })
      .addCase(deleteAssignmentType.fulfilled, (state, { payload }) => {
        state.data = state.data.filter((item) => item._id !== payload.id);
        state.loading = "idle";
      })
      .addCase(deleteAssignmentType.rejected, (state, action) => {
        console.error("Delete assignment type rejected:", action.error.message);
        state.error = action.error.message;
        state.loading = "idle";
      });
  },
});

export const { clearSelectedAssignmentType } = assignmentTypesSlice.actions;

export const assignmentTypesEntities = ({ assignmentTypes }) => assignmentTypes;

export default assignmentTypesSlice.reducer;
